import React, { useEffect, useState } from 'react';
import { Card, Tabs, Input, Row, Col, Tag, Typography, Spin, message, Button, Select } from 'antd';
import {
  FileTextOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  FileSearchOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import customerApi from '../services/customerApi';
import { Quotation } from '../services/quotations';
import { Invoice } from '../services/invoices';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

type TabKey = 'quotation' | 'unpaid-invoice' | 'estimate' | 'pricing';

export const CustomerBilling: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('unpaid-invoice');
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [quotationStatus, setQuotationStatus] = useState('');
  const [invoiceCustomerSearch, setInvoiceCustomerSearch] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');

  useEffect(() => {
    if (activeTab === 'quotation') {
      fetchQuotations();
    } else if (activeTab === 'unpaid-invoice') {
      fetchInvoices();
    }
  }, [activeTab, customerSearch, quotationStatus, invoiceCustomerSearch, invoiceStatus]);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const response = await customerApi.get('/customer/quotations');
      let data = response.data.data || response.data || [];
      
      data = data.filter((q: Quotation) => (q.status || '').toLowerCase() !== 'draft');

      if (customerSearch) {
        const query = customerSearch.toLowerCase();
        data = data.filter((q: Quotation) => {
          const name = (q.customer?.name || '').toLowerCase();
          return name.includes(query);
        });
      }

      if (quotationStatus) {
        data = data.filter((q: Quotation) => q.status === quotationStatus);
      }
      
      setQuotations(data);
    } catch (error: any) {
      message.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await customerApi.get('/customer/invoices');
      let data = response.data.data || response.data || [];
      
      data = data.map((inv: Invoice) => {
        // Calculate total from invoice items
        const itemsTotal = inv.items?.reduce((sum, item) => {
          const itemTotal = parseFloat(item.total_price?.toString() || '0');
          return sum + itemTotal;
        }, 0) || 0;
        
        // Calculate total payments
        const totalPaid = inv.payments?.reduce((sum, p) => sum + (p.payment_amount || 0), 0) || 0;
        
        return { ...inv, _itemsTotal: itemsTotal, _totalPaid: totalPaid };
      });

      if (invoiceStatus) {
        data = data.filter((inv: Invoice & { _itemsTotal?: number; _totalPaid?: number }) => {
          const itemsTotal = inv._itemsTotal || 0;
          const totalPaid = inv._totalPaid || 0;
          if (invoiceStatus === 'unpaid') {
            return totalPaid < itemsTotal;
          }
          if (invoiceStatus === 'paid') {
            return itemsTotal > 0 && totalPaid >= itemsTotal;
          }
          return true;
        });
      }

      if (invoiceCustomerSearch) {
        const query = invoiceCustomerSearch.toLowerCase();
        data = data.filter((inv: Invoice) => (inv.customer?.name || '').toLowerCase().includes(query));
      }

      setInvoices(data as Invoice[]);
    } catch (error: any) {
      message.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  const formatDateLong = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY');
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return 'RM 0.00';
    return `RM ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleApproveQuotation = async (quotationId: number) => {
    try {
      await customerApi.post(`/customer/quotations/${quotationId}/approve`);
      message.success('Quotation approved successfully. Invoice has been created.');
      fetchQuotations(); // Refresh quotations list
    } catch (error: any) {
      console.error('Failed to approve quotation:', error);
      let errorMessage = 'Failed to approve quotation';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    }
  };

  const tabItems = [
    {
      key: 'quotation',
      label: (
        <span>
          <FileTextOutlined />
          Quotation
        </span>
      ),
    },
    {
      key: 'unpaid-invoice',
      label: (
        <span>
          <ExclamationCircleOutlined />
          Invoice
        </span>
      ),
    },
    {
      key: 'estimate',
      label: (
        <span>
          <FileSearchOutlined />
          Estimate
        </span>
      ),
    },
    {
      key: 'pricing',
      label: (
        <span>
          <DollarOutlined />
          Pricing
        </span>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>
        Billing
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabKey)}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />

      {activeTab === 'quotation' && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
          <Input
            placeholder="Search customer"
            style={{ width: 240 }}
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            allowClear
          />
          <Select
            placeholder="Select status"
            style={{ width: 200 }}
            value={quotationStatus || undefined}
            onChange={(value) => setQuotationStatus(value || '')}
            allowClear
          >
            <Option value="Pending">Pending</Option>
            <Option value="Approved">Approved</Option>
          </Select>
        </div>
      )}
      {activeTab === 'unpaid-invoice' && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
          <Input
            placeholder="Search customer"
            style={{ width: 240 }}
            value={invoiceCustomerSearch}
            onChange={(e) => setInvoiceCustomerSearch(e.target.value)}
            allowClear
          />
          <Select
            placeholder="Select status"
            style={{ width: 200 }}
            value={invoiceStatus || undefined}
            onChange={(value) => setInvoiceStatus(value || '')}
            allowClear
          >
            <Option value="unpaid">Unpaid</Option>
            <Option value="paid">Paid</Option>
          </Select>
        </div>
      )}

      {loading ? (
        <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />
      ) : (
        <>
          {activeTab === 'quotation' && (
            <Row gutter={[16, 16]}>
              {quotations.map((quotation) => {
                const firstTask = quotation.tasks?.[0];
                const firstItem = quotation.items?.[0];
                const taskTypeName = firstTask?.taskType?.name || firstItem?.taskType?.name || '-';
                const taskName = firstTask?.taskTemplate?.name || firstItem?.taskTemplate?.name || '-';
                const taskNumber = firstTask?.id ? `#${firstTask.id.toString().padStart(7, '0')}` : '-';
                const itemsTotal = quotation.items?.reduce((sum, item) => {
                  const amount = parseFloat(item.amount?.toString() || '0');
                  return sum + amount;
                }, 0) || 0;
                const taskAmount = firstTask?.amount ? parseFloat(firstTask.amount.toString()) : 0;
                const quotationAmount = itemsTotal || taskAmount;

                return (
                  <Col key={quotation.id} xs={24} sm={12} md={12} lg={12}>
                    <Card
                      title={`${quotation.quotation_number} - ${quotation.customer?.name || '-'}`}
                      bodyStyle={{ padding: 0 }}
                      style={{
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate(`/customer/quotation/${quotation.id}`)}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', width: '140px', backgroundColor: '#fafafa' }}>
                              <strong>Customer</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {quotation.customer?.name || '-'}
                            </td>
                            <td style={{ padding: '12px', width: '140px', backgroundColor: '#fafafa' }}>
                              <strong>Quotation No.</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {quotation.quotation_number}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Type</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {taskTypeName}
                            </td>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Date</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {formatDate(quotation.date)}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Year</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {dayjs(quotation.date).year()}
                            </td>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Link to Invoice No.</strong>
                            </td>
                            <td style={{ padding: '12px', color: '#722ed1' }}>
                              {quotation.invoice?.invoice_number || quotation.invoice_id || '-'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Task</strong>
                            </td>
                            <td colSpan={3} style={{ padding: '12px' }}>
                              {taskName}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Link to Task No.</strong>
                            </td>
                            <td colSpan={3} style={{ padding: '12px', color: '#722ed1' }}>
                              {taskNumber}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Amount</strong>
                            </td>
                            <td colSpan={3} style={{ padding: '12px' }}>
                              {quotationAmount > 0 ? formatCurrency(quotationAmount) : '-'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    {quotation.status === 'Pending' && (
                      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApproveQuotation(quotation.id);
                          }}
                        >
                          Approve
                        </Button>
                      </div>
                    )}
                  </Card>
                </Col>
                );
              })}
              {quotations.length === 0 && (
                <Col span={24}>
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No quotations found
                  </div>
                </Col>
              )}
            </Row>
          )}

          {activeTab === 'unpaid-invoice' && (
            <Row gutter={[16, 16]}>
              {invoices.map((invoice) => {
                // Calculate total from invoice items
                const itemsTotal = invoice.items?.reduce((sum, item) => {
                  const itemTotal = parseFloat(item.total_price?.toString() || '0');
                  return sum + itemTotal;
                }, 0) || 0;
                
                // Calculate total payments
                const totalPaid = invoice.payments?.reduce((sum, p) => sum + (p.payment_amount || 0), 0) || 0;
                
                const isPaid = itemsTotal > 0 && totalPaid >= itemsTotal;
                const invoiceTask = invoice.items?.[0]?.taskTemplate?.name
                  || invoice.quotation?.tasks?.[0]?.taskTemplate?.name
                  || '-';
                const invoiceType = invoice.items?.[0]?.taskType?.name
                  || invoice.quotation?.tasks?.[0]?.taskType?.name
                  || '-';
                const invoiceTaskNumber = invoice.quotation?.tasks?.[0]?.id
                  ? `#${invoice.quotation.tasks[0].id.toString().padStart(7, '0')}`
                  : '-';
                const invoiceHeaderNumber = invoice.quotation?.quotation_number || invoice.invoice_number;

                return (
                  <Col key={invoice.id} xs={24} sm={12} md={12} lg={12}>
                    <Card
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                          <span>{`${invoiceHeaderNumber} - ${invoice.customer?.name || '-'}`}</span>
                          <Tag
                            color={isPaid ? 'green' : 'red'}
                            style={{
                              borderRadius: 4,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                display: 'inline-block',
                              }}
                            />
                            {isPaid ? 'Paid' : 'Unpaid'}
                          </Tag>
                        </div>
                      }
                      bodyStyle={{ padding: 0 }}
                      style={{
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate(`/customer/invoice/${invoice.id}`)}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', width: '140px', backgroundColor: '#fafafa' }}>
                              <strong>Customer</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {invoice.customer?.name || '-'}
                            </td>
                            <td style={{ padding: '12px', width: '140px', backgroundColor: '#fafafa' }}>
                              <strong>Invoice No.</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {invoice.invoice_number}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Type</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {invoiceType}
                            </td>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Date</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {formatDate(invoice.date)}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Year</strong>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {dayjs(invoice.date).year()}
                            </td>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Link to Quotation No.</strong>
                            </td>
                            <td style={{ padding: '12px', color: '#722ed1' }}>
                              {invoice.quotation?.quotation_number || '-'}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Task</strong>
                            </td>
                            <td colSpan={3} style={{ padding: '12px' }}>
                              {invoiceTask}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Link to Task No.</strong>
                            </td>
                            <td colSpan={3} style={{ padding: '12px', color: '#722ed1' }}>
                              {invoiceTaskNumber}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '12px', backgroundColor: '#fafafa' }}>
                              <strong>Amount</strong>
                            </td>
                            <td colSpan={3} style={{ padding: '12px' }}>
                              {formatCurrency(itemsTotal)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Card>
                  </Col>
                );
              })}
              {invoices.length === 0 && (
                <Col span={24}>
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No unpaid invoices found
                  </div>
                </Col>
              )}
            </Row>
          )}

          {activeTab === 'estimate' && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              Estimate content coming soon
            </div>
          )}

          {activeTab === 'pricing' && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              Pricing content coming soon
            </div>
          )}
        </>
      )}
    </div>
  );
};
